import React, { useState, useMemo } from "react";
import moment from "moment/moment";
import SearchInput from "./SearchInput";
import CustomSelect from "./CustomSelect";
import ArrowDownIcon from "../../assets/icons/arrowDown";
import PrevIcon from "../../assets/icons/prev";
import NextIcon from "../../assets/icons/next";
import { Link } from "react-router-dom";
import { hasPermission, isAdmin } from "../../Utils/auth";
import { FiEdit } from "react-icons/fi";
import { IoMdEye } from "react-icons/io";
import EnableDisableRadio from "./EnableDisableRadio";
import Popup from "./Popup";

const EmployeeTable = ({ data, designationOptions, handleDisableEnable }) => {
  const [activeTab, setActiveTab] = useState("active");
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [pageInput, setPageInput] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedUserStatus, setSelectedUserStatus] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);

  const activeCount = useMemo(
    () => data.filter((emp) => emp.user_status === 1).length,
    [data],
  );

  const inactiveCount = useMemo(
    () => data.filter((emp) => emp.user_status === 0).length,
    [data],
  );

  /* ---------------- FILTER LOGIC ---------------- */

  const filteredData = data.filter((row) => {
    return (
      row.employee_name.toLowerCase().includes(search.toLowerCase()) &&
      (locationFilter ? row?.location === locationFilter : true) &&
      (activeTab === "active" ? row.user_status === 1 : row.user_status === 0)
    );
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const currentRows = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDisable = (emp, userStatus) => {
    setSelectedEmployee(emp);
    setSelectedUserStatus(userStatus === 1 ? "Disable" : "Enable");
    setDisableOpen(true);
  };

  const locationOptions = [
    { label: "All Location", value: "" },
    { label: "Dubai", value: "Dubai" },
    { label: "India", value: "India" },
  ];

  return (
    <div className="w-full rounded-lg">
      {/* ---------- Tabs ---------- */}

      <div className="flex items-center gap-3 mb-6 bg-[#f6f7fb] p-1 rounded-lg w-fit">
        <button
          onClick={() => {
            setActiveTab("active");
            setCurrentPage(1);
          }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            activeTab === "active"
              ? "bg-white shadow text-[#de2b26]"
              : "text-[#142b70]/70 hover:text-[#142b70]"
          }`}
        >
          Active Employees ({activeCount})
        </button>

        <button
          onClick={() => {
            setActiveTab("inactive");
            setCurrentPage(1);
          }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            activeTab === "inactive"
              ? "bg-white shadow text-[#de2b26]"
              : "text-[#142b70]/70 hover:text-[#142b70]"
          }`}
        >
          Non Active Employees ({inactiveCount})
        </button>
      </div>

      {/* ---------- Filters ---------- */}

      <div className="flex justify-between items-start md:flex-row gap-3 mb-4">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name..."
          fontSize="sm"
        />

        <div className="flex flex-grow-0 flex-shrink-0 gap-4">
          <CustomSelect
            options={locationOptions}
            placeholder="Location"
            value={locationFilter}
            onChange={(loc) => setLocationFilter(loc.value)}
            width="w-[200px]"
            height="h-[36px]"
            fontSize="sm"
            optionFontSize="sm"
          />
        </div>
      </div>

      {/* ---------- Table ---------- */}

      <div className="overflow-x-auto">
        <table className="min-w-[800px] table-auto w-full text-sm">
          <thead className="sticky top-0 bg-white z-0">
            <tr className="h-[52px] border border-l-0 border-r-0 border-[#a48572]/10 text-[#142b70]">
              <th className="min-w-[150px] px-4 text-left font-semibold">
                Employee Name
              </th>
              <th className="px-4 text-left font-semibold">Designation</th>
              <th className="min-w-[150px] px-4 text-center font-semibold">
                Team Leader
              </th>
              <th className="min-w-[150px] px-4 text-center font-semibold">
                Joining Date
              </th>
              <th className="min-w-[150px] px-4 text-center font-semibold">
                Phone Number
              </th>
              <th className="px-4 text-center font-semibold">Location</th>
              <th className="px-4 text-center font-semibold">Status</th>
              <th className="px-4 text-center font-semibold">Action</th>
            </tr>
          </thead>

          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((row, idx) => (
                <tr key={idx} className="hover:bg-[#f9f9f9]">
                  <td className="py-3 pl-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-[#de2b26] flex items-center justify-center text-white text-sm">
                        {row.employee_name ? row.employee_name.charAt(0) : "N"}
                      </div>

                      <p className="text-sm text-[#142b70]/80">
                        {row.employee_name || "N/A"}
                      </p>
                    </div>
                  </td>

                  <td className="text-left px-4 py-3">
                    {row.designation ? row.designation.title : "N/A"}
                  </td>

                  <td className="text-center py-3">
                    {row.team_leader || "N/A"}
                  </td>

                  <td className="text-center py-3">
                    {row.joining_date
                      ? moment(row.joining_date).format("DD MMM YYYY")
                      : "N/A"}
                  </td>

                  <td className="text-center py-3">{row.phone_num || "N/A"}</td>

                  <td className="text-center py-3">{row.location || "N/A"}</td>

                  <td className="text-center py-3">
                    <div
                      className={`flex justify-center mx-auto items-center w-[90px] px-2 py-1 rounded ${
                        row.user_status === 0
                          ? "bg-[#de2b26]/10"
                          : "bg-[#32936f]/[0.16]"
                      }`}
                    >
                      <p
                        className={`text-xs ${
                          row.user_status === 0
                            ? "text-[#142b70]"
                            : "text-[#519c66]"
                        }`}
                      >
                        {row.user_status === 1 ? "Active" : "Not Active"}
                      </p>
                    </div>
                  </td>

                  <td className="text-right py-3 px-3">
                    {isAdmin() ||
                    (hasPermission("edit_employees") &&
                      hasPermission("view_employees") &&
                      hasPermission("disable_employees")) ? (
                      <div className="flex justify-center gap-2">
                        <Link
                          to={`/view-employee/${row.id}`}
                          className="px-2 py-1 rounded bg-[#de2b26]/10"
                        >
                          <IoMdEye className="text-xs text-[#142b70]" />
                        </Link>

                        <Link
                          to={`/employee/${row.id}`}
                          className="px-2 py-1 rounded bg-[#de2b26]/10"
                        >
                          <FiEdit className="text-xs text-[#142b70]" />
                        </Link>

                        <EnableDisableRadio
                          enabled={row.user_status === 1}
                          handleStatusToggle={() =>
                            handleDisable(row, row.user_status)
                          }
                        />
                      </div>
                    ) : (
                      <span className="text-xs text-[#142b70]">
                        Not Allowed
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* ---------- Popup ---------- */}

        <Popup
          isOpen={disableOpen}
          onClose={() => setDisableOpen(false)}
          title="Disable Employee"
        >
          <div className="flex flex-col gap-6 mt-[30px] mb-[30px]">
            <p className="text-sm text-[#142b70]/80">
              Are you sure and want to {selectedUserStatus.toLowerCase()}{" "}
              <b>{selectedEmployee?.employee_name}</b>?
            </p>
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={() => setDisableOpen(false)}
              className="px-10 py-3 rounded-md border border-[#de2b26] text-[#de2b26]"
            >
              Cancel
            </button>

            <button
              onClick={() =>
                handleDisableEnable(selectedUserStatus, selectedEmployee?.id)
              }
              className="px-10 py-3 rounded-md bg-[#de2b26] text-white"
            >
              {selectedUserStatus}
            </button>
          </div>
        </Popup>
      </div>

      {/* ---------- Pagination ---------- */}

      <div className="flex justify-between items-center mt-4">
        <p className="text-sm text-[#142b70]/80">
          Page {currentPage} of {totalPages}
        </p>

        <div className="flex gap-3">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <PrevIcon color="#142b70" />
          </button>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <NextIcon color="#142b70" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTable;
